import React, { Component } from 'react';
import styled from 'styled-components';
import ChevronDown from '../../../svgs/ChevronDown';

const DropdownWrapper = styled.div`
  position: relative;
  width: ${({ theme }) => theme.unit(40)};
  max-width: 100%;
`;

const DropdownFieldset = styled.fieldset`
  margin: 0;
  padding: 0 ${({ theme }) => theme.unit(2)} ${({ theme }) => theme.unit(1)};
  border: 1px solid
    ${({ theme, $isOpen }) => ($isOpen ? theme.c.border.focus : theme.c.border.secondary)};
  box-shadow: ${({ theme, $isOpen }) =>
    $isOpen ? `inset 0 0 0 1px ${theme.c.border.focus}` : 'none'};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.c.bg.primary};

  &:focus-within {
    border-color: ${({ theme }) => theme.c.border.focus};
    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.c.border.focus};
  }
`;

const DropdownLegend = styled.legend`
  padding: 0 ${({ theme }) => theme.unit(0.5)};
  margin-left: ${({ theme }) => theme.unit(1)};
  ${({ theme }) => theme.typographyRebrand.body.sm}
  color: ${({ theme }) => theme.c.text.primary};
  background-color: ${({ theme }) => theme.c.bg.primary};
`;

const DropdownButton = styled.button`
  width: 100%;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
  padding: ${({ theme }) => theme.unit(1)} 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.c.text.primary};

  &:focus-visible {
    outline: none;
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
`;

const SelectedOptionContainer = styled.span`
  ${({ theme }) => theme.typographyRebrand.body.md}
  color: ${({ theme }) => theme.c.text.primary};
`;

const IconContainer = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.c.icon.primary};
`;

const DropdownMenu = styled.ul`
  box-sizing: border-box;
  position: absolute;
  top: 100%;
  right: 0;
  width: 100%;
  z-index: ${({ theme }) => theme.elevation[2]};
  background-color: ${({ theme }) => theme.c.bg.primary};
  border: 1px solid ${({ theme }) => theme.c.border.primary};
  border-top: none;
  border-radius: 0 0 ${({ theme }) => theme.radius.sm} ${({ theme }) => theme.radius.sm};
  padding: 0;
  margin: -1px 0 0;
  list-style: none;
`;

const DropdownItem = styled.li`
  box-sizing: border-box;
  list-style: none;
  ${({ theme }) => theme.typographyRebrand.body.sm}
  color: ${({ theme }) => theme.c.text.primary};
  background-color: ${({ theme, $isSelected, $isFocused }) => {
    if ($isSelected) return theme.c.bg['feature-1'];
    if ($isFocused) return theme.c.bg.secondary;
    return theme.c.bg.primary;
  }};
  padding: ${({ theme }) => theme.unit(2)};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.c.bg.secondary};
  }

  &:focus {
    outline: 1px solid ${({ theme, $isSelected }) => (
      $isSelected ? 'none' : theme.c.border.focus
    )} !important;
    box-shadow: ${({ theme, $isSelected }) => `inset 0 0 0 1px ${
      $isSelected
        ? 'transparent'
        : theme.c.border.focus}
    `};
    
    background-color: ${({ theme, $isSelected }) => (
      $isSelected ? theme.c.bg['feature-1'] : theme.c.bg.primary
    )};
  }
`;

class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      focusedIndex: -1,
    };

    this.dropdownButtonRef = React.createRef();
    this.dropdownMenuRef = React.createRef();
    this.dropdownMenuItemsRefs = [];

    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    window.document.addEventListener('keydown', this.handleKeyDown);
    window.document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    window.document.removeEventListener('keydown', this.handleKeyDown);
    window.document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside(event) {
    if (
      this.dropdownButtonRef.current
      && !this.dropdownButtonRef.current.contains(event.target)
      && this.dropdownMenuRef.current
      && !this.dropdownMenuRef.current.contains(event.target)
    ) {
      this.setState({ isOpen: false });
    }
  }

  handleOptionClick(option) {
    const { onSelect } = this.props;
    onSelect(option);
    this.setState({ isOpen: false, focusedIndex: -1 });
    if (this.dropdownButtonRef.current) {
      this.dropdownButtonRef.current.blur();
    }
  }

  handleKeyDown(e) {
    const { options } = this.props;
    const { focusedIndex } = this.state;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setState({ focusedIndex: (focusedIndex + 1) % options.length });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.setState({ focusedIndex: (focusedIndex - 1 + options.length) % options.length });
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (focusedIndex >= 0) {
        this.setState({ isOpen: false }, () => {
          if (this.dropdownButtonRef.current) {
            this.dropdownButtonRef.current.focus();
          }
        });
        this.handleOptionClick(options[focusedIndex].value);
      }
    } else if (e.key === 'Escape') {
      this.setState({ isOpen: false }, () => {
        if (this.dropdownButtonRef.current) {
          this.dropdownButtonRef.current.focus();
        }
      });
    }
  }

  toggleDropdown() {
    const {
      options,
      selectedOption,
    } = this.props;
    const {
      isOpen,
    } = this.state;

    const focusedIndex = options.findIndex(({ value }) => (
      selectedOption === value
    ));

    this.setState((prevState) => ({
      isOpen: !prevState.isOpen,
      focusedIndex,
    }), () => {
      if (!isOpen && this.dropdownMenuItemsRefs[focusedIndex]) {
        this.dropdownMenuItemsRefs[focusedIndex].focus();
      }
      if (!isOpen && this.dropdownButtonRef.current) {
        this.dropdownButtonRef.current.blur();
      }
    });
  }

  render() {
    const {
      options,
      label,
      selectedOption,
    } = this.props;
    const { isOpen, focusedIndex } = this.state;

    const selectedOptionItem = options.find(
      ({ value }) => value === selectedOption
    );
    const selectedOptionLabel = selectedOptionItem ? selectedOptionItem.label : '';

    return (
      <DropdownWrapper>
        <DropdownFieldset $isOpen={isOpen}>
          <DropdownLegend>{label}</DropdownLegend>
          <DropdownButton
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            onClick={this.toggleDropdown}
            ref={this.dropdownButtonRef}
            type="button"
          >
            <SelectedOptionContainer>{selectedOptionLabel}</SelectedOptionContainer>
            <IconContainer>
              <ChevronDown />
            </IconContainer>
          </DropdownButton>
        </DropdownFieldset>
        {isOpen && (
          <DropdownMenu
            role="listbox"
            ref={this.dropdownMenuRef}
          >
            {options.map((option, index) => {
              const isItemSelected = selectedOption === option.value;
              const isItemFocused = focusedIndex === index;

              return (
                <DropdownItem
                  key={option.value}
                  role="option"
                  $isFocused={isItemFocused}
                  $isSelected={isItemSelected}
                  aria-selected={isItemSelected}
                  tabIndex={-1}
                  onClick={() => this.handleOptionClick(option.value)}
                  onMouseEnter={() => this.setState({ focusedIndex: index })}
                  ref={(el) => {
                    this.dropdownMenuItemsRefs[index] = el;
                    return el;
                  }}
                >
                  {option.label}
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        )}
      </DropdownWrapper>
    );
  }
}

export default Dropdown;
